import { useAuth0 } from '@auth0/auth0-react';
import LoginIcon from '@mui/icons-material/Login';
import {
  Avatar,
  Button,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UnsolicitedResultsIcon } from 'src/features/external-labs/components/unsolicited-results/UnsolicitedResultsIcon';
import { getFullestAvailableName, PROJECT_NAME } from 'utils';
import { dataTestIds } from '../../constants/data-test-ids';
import useEvolveUser from '../../hooks/useEvolveUser';

export const UserMenu: FC = () => {
  const { t } = useTranslation();
  // ✅ TODOS LOS HOOKS AL PRINCIPIO - SIEMPRE SE EJECUTAN EN EL MISMO ORDEN
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const user = useEvolveUser();

  const name = user?.profileResource && (getFullestAvailableName(user.profileResource, true) ?? `${PROJECT_NAME} Team`);
  const suffix = user?.profileResource?.name?.[0]?.suffix?.[0];

  // ✅ AHORA SÍ: Returns condicionales DESPUÉS de todos los hooks
  // Si no está autenticado, mostrar botón de login
  if (!isAuthenticated && !isLoading) {
    return (
      <Button
        variant="contained"
        color="primary"
        startIcon={<LoginIcon />}
        onClick={() => {
          loginWithRedirect({
            appState: {
              returnTo: window.location.pathname,
            },
          }).catch((error) => {
            console.error('Error en login:', error);
            enqueueSnackbar('Error al iniciar sesión. Por favor, inténtelo de nuevo.', { variant: 'error' });
          });
        }}
        sx={{ ml: 2 }}
      >
        Login
      </Button>
    );
  }

  // Si está cargando, mostrar un placeholder
  if (isLoading) {
    const auth0Key = Object.keys(localStorage).find((key) => key.startsWith('@@auth0spajs@@'));
    const sessionData = JSON.parse(localStorage.getItem(auth0Key ?? '') ?? '{}');
    console.log(sessionData);
    return (
      <ListItem disablePadding sx={{ width: 'fit-content' }}>
        <ListItemButton disabled>
          <ListItemAvatar sx={{ minWidth: 'auto', mr: { xs: '0', sm: 2 } }}>
            <Avatar sx={{ bgcolor: 'grey.400' }} />
          </ListItemAvatar>
        </ListItemButton>
      </ListItem>
    );
  }

  // Usuario autenticado - mostrar menú normal

  return (
    <>
      <UnsolicitedResultsIcon />
      <ListItem disablePadding sx={{ width: 'fit-content' }}>
        <ListItemButton onClick={(event: MouseEvent<HTMLElement>) => setAnchorElement(event.currentTarget)}>
          <ListItemAvatar sx={{ minWidth: 'auto', mr: { xs: '0', sm: 2 } }}>
            <Avatar sx={{ bgcolor: 'primary.main' }} alt={name} src={user?.profileResource?.photo?.[0]?.url} />
          </ListItemAvatar>
          <ListItemText
            data-testid={dataTestIds.header.userName}
            sx={{ display: { xs: 'none', sm: 'block' } }}
            primary={name}
            secondary={suffix}
          />
        </ListItemButton>
      </ListItem>
      <Menu
        id="user-menu"
        anchorEl={anchorElement}
        open={anchorElement !== null}
        onClose={() => setAnchorElement(null)}
      >
        <Link to="/logout" style={{ textDecoration: 'none' }}>
          <MenuItem>
            <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>
              {t('userMenu.logout')}
            </Typography>
          </MenuItem>
        </Link>
      </Menu>
    </>
  );
};
